import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Carousel1 from '../Carousel1';
import Cards2 from '../Cards2';
import GTranslate from '../GTanslate';
import { 
  FaChevronDown, 
  FaGlobe, 
  FaIndustry, 
  FaBuilding, 
  FaAward,
  FaChartLine,
  FaHandshake,
  FaLeaf,
  FaRocket,
  FaUsers,
  FaShieldAlt,
  FaCogs
} from 'react-icons/fa';

const Home = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollProgress((totalScroll / windowHeight) * 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContent = () => {
    document.querySelector('section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-100 via-gray-50 to-white font-montserrat overflow-hidden">
      {/* Fixed Header - Scroll Progress */}
      {/* <div className="fixed top-12 left-0 w-full h-1 bg-gray-300 z-50">
        <motion.div 
          className="h-full bg-gradient-to-r from-red-600 via-red-700 to-red-800"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: 0 }}
        />
      </div> */}

      {/* Language Selector - Professional */}
      <div className="fixed top-6 right-6 z-50">
          <GTranslate />
      </div>

      {/* Hero Section - Corporate */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-100 to-white">
        {/* Subtle Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(to right, #dc2626 1px, transparent 1px),
                             linear-gradient(to bottom, #dc2626 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />

        {/* Main Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-left"
            >
              {/* Logo/Branding */}
              <div className="flex items-center gap-4 mb-8">
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-red-700 to-red-600 rounded-xl flex items-center justify-center shadow-2xl shadow-red-500/30"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <FaIndustry className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">YÜCEL GRUBU</h1>
                  <p className="text-sm text-red-600 font-semibold uppercase tracking-wider">1949'dan Beri</p>
                </div>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Kompozit Teknolojide
                <span className="block text-red-700 mt-2">Global Referans</span>
              </h1>

              <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl">
                70+ yıllık mükemmellik geleneği ile <span className="font-semibold text-red-700">6 kıtada</span> kompozit teknolojilerinin 
                sınırlarını genişleten, endüstriyel inovasyonun öncüsü.
              </p>

              {/* Stats - Professional */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                {[
                  { value: '70+', label: 'Yıllık Deneyim', icon: FaAward, color: 'text-red-600' },
                  { value: '6', label: 'Kıtada Varlık', icon: FaGlobe, color: 'text-red-600' },
                  { value: '40+', label: 'Ülkede Operasyon', icon: FaBuilding, color: 'text-red-600' },
                  { value: '1000+', label: 'Prestij Proje', icon: FaChartLine, color: 'text-red-600' }
                ].map((stat, index) => (
                  <motion.div 
                    key={index}
                    whileHover={{ y: -5, scale: 1.05 }}
                    className="text-center p-4 bg-white rounded-xl shadow-lg border border-gray-200"
                  >
                    <stat.icon className={`w-6 h-6 ${stat.color} mx-auto mb-2`} />
                    <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(220, 38, 38, 0.3)" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContent}
                  className="px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center gap-3 group"
                >
                  Keşfet
                  <FaChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-4 bg-white text-gray-800 rounded-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-300 flex items-center gap-3 group"
                >
                  <FaHandshake className="w-4 h-4 text-red-600 group-hover:scale-110 transition-transform" />
                  İş Birlikleri
                </motion.button>
              </div>
            </motion.div>

            {/* Right Column - Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Floating Cards with Red Accents - Updated */}
              <div className="relative h-[600px]">
                {[
                  { 
                    top: '10%', 
                    left: '5%', 
                    delay: 0, 
                    color: 'from-red-600 to-red-700',
                    title: 'Kompozit Teknolojileri',
                    description: '6 kıtada referans projeler',
                    icon: <FaCogs className="w-5 h-5 text-white" />,
                    achievements: ['70+ Yıl', '40+ Ülke', '200+ Patent']
                  },
                  { 
                    top: '30%', 
                    left: '60%', 
                    delay: 0.2, 
                    color: 'from-gray-800 to-gray-900',
                    title: 'Global Operasyonlar',
                    description: '6 kıtada kesintisiz hizmet',
                    icon: <FaGlobe className="w-5 h-5 text-white" />,
                    achievements: ['6 Kıta', '24/7 Destek', 'Yerel Ekipler']
                  },
                  { 
                    top: '60%', 
                    left: '20%', 
                    delay: 0.4, 
                    color: 'from-red-700 to-red-800',
                    title: 'Sürdürülebilirlik',
                    description: 'Çevre dostu çözümler',
                    icon: <FaLeaf className="w-5 h-5 text-white" />,
                    achievements: ['ISO 14001', 'Karbon Nötr', '%100 Geri Dönüşüm']
                  }
                ].map((pos, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 50, rotate: idx % 2 === 0 ? -5 : 5 }}
                    animate={{ opacity: 1, y: 0, rotate: 0 }}
                    transition={{ duration: 0.6, delay: pos.delay }}
                    className={`absolute w-80 h-56 bg-white rounded-2xl shadow-2xl border-l-4 border-red-600 overflow-hidden group`}
                    style={{ top: pos.top, left: pos.left }}
                    whileHover={{ 
                      y: -15, 
                      rotate: 0, 
                      scale: 1.05,
                      boxShadow: "0 25px 50px -12px rgba(220, 38, 38, 0.25)",
                      transition: { duration: 0.3 } 
                    }}
                  >
                    {/* Gradient Header */}
                    <div className={`h-24 bg-gradient-to-r ${pos.color} relative overflow-hidden`}>
                      {/* Animated Background Pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-2 right-2 w-8 h-8 rounded-full border-2 border-white/30"></div>
                        <div className="absolute bottom-2 left-2 w-6 h-6 rounded-full border border-white/20"></div>
                      </div>
                      
                      <div className="relative z-10 p-6 h-full flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30`}>
                            {pos.icon}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-white">{pos.title}</h3>
                            <p className="text-sm text-red-100 font-medium">{pos.description}</p>
                          </div>
                        </div>
                        
                        {/* Continent Indicator */}
                        <div className="flex items-center gap-1">
                          {[...Array(6)].map((_, i) => (
                            <div 
                              key={i} 
                              className="w-2 h-2 rounded-full bg-white/50 animate-pulse"
                              style={{ animationDelay: `${i * 0.1}s` }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-6">
                      {/* Achievements Grid */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {pos.achievements.map((achievement, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: pos.delay + (index * 0.1) }}
                            className="text-center group/item"
                          >
                            <div className="text-xs font-semibold text-gray-500 mb-1">
                              {achievement.split(' ')[0]}
                            </div>
                            <div className="text-sm font-bold text-gray-800">
                              {achievement.split(' ').slice(1).join(' ')}
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Progress Bar - 6 Continents */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600">Global Varlık</span>
                          <span className="text-xs font-bold text-red-600">6/6 Kıta</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 1, delay: pos.delay + 0.3 }}
                            className="h-full bg-gradient-to-r from-red-500 to-red-600"
                          />
                        </div>
                        
                        {/* Continent Dots */}
                        <div className="flex justify-between mt-2 px-1">
                          {['Avrupa', 'Asya', 'Afrika', 'Amerika'].map((continent, i) => (
                            <div key={i} className="relative">
                              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center">
                                <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                              </div>
                              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="text-[10px] font-medium bg-gray-800 text-white px-2 py-1 rounded whitespace-nowrap">
                                  {continent}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Hover Indicator */}
                      <motion.div
                        initial={{ opacity: 0, width: 0 }}
                        whileHover={{ opacity: 1, width: "100%" }}
                        className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent"
                      />
                    </div>

                    {/* Corner Accent */}
                    <div className="absolute top-0 right-0 w-8 h-8">
                      <div className="absolute top-0 right-0 w-0 h-0 border-t-[32px] border-t-red-600 border-l-[32px] border-l-transparent"></div>
                      <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-white/80"></div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="text-center">
            <div className="text-sm text-gray-600 mb-2 font-medium">Daha Fazlasını Keşfedin</div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center mx-auto shadow-lg">
              <FaChevronDown className="w-4 h-4 text-white" />
            </div>
          </div>
        </motion.div>
      </section>

      {/* Carousel Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-2 h-12 bg-gradient-to-b from-red-600 to-red-700 rounded-full" />
              <h2 className="text-4xl font-bold text-gray-900">
                Global <span className="text-red-700">Başarı Hikayelerimiz</span>
              </h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              6 kıtada gerçekleştirdiğimiz prestijli projeler ve endüstriyel çözümler
            </p>
          </div>
          <Carousel1 />
        </div>
      </section>

      {/* Global Expertise Section - Updated with 3 Areas */}
      <section className="py-24 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-4 mb-6"
            >
              <div className="w-16 h-1 bg-gradient-to-r from-red-600 to-red-700 rounded-full" />
              <span className="text-sm font-semibold text-red-600 uppercase tracking-wider">
                KÜRESEL UZMANLIK ALANLARIMIZ
              </span>
              <div className="w-16 h-1 bg-gradient-to-r from-red-700 to-red-600 rounded-full" />
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-red-700">6 Kıtada</span> Fark Yaratan
              <br />
              <span className="text-gray-800">Kritik Yetkinlikler</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              70+ yıllık mühendislik birikimi ile 6 kıtada aynı kalite standartlarında
              uyguladığımız, sektör lideri üç temel uzmanlık alanı
            </p>
          </div>

          {/* Three Core Competencies Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Kompozit Mühendislik",
                subtitle: "İleri Malzeme Çözümleri",
                icon: FaCogs,
                description: "Yüksek performanslı kompozit malzeme tasarımı, geliştirme ve üretiminde 6 kıtada referans gösterilen mühendislik çözümleri",
                achievements: [
                  "200+ patentli kompozit formülasyon",
                  "ISO 9001:2015 sertifikalı tesisler",
                  "40+ ülkede uygulama deneyimi",
                  "AS9100D havacılık standardı"
                ],
                color: "from-red-600 to-red-700",
                delay: 0
              },
              {
                title: "Endüstriyel İnovasyon",
                subtitle: "Akıllı Üretim Sistemleri",
                icon: FaRocket,
                description: "Dijital dönüşüm ve endüstri 4.0 teknolojileri ile entegre, verimlilik odaklı üretim sistemleri ve otomasyon çözümleri",
                achievements: [
                  "6 kıtada 150+ otomasyon projesi",
                  "%30 enerji verimliliği artışı",
                  "Sıfır hata üretim metodolojisi",
                  "Gerçek zamanlı proses kontrol"
                ],
                color: "from-gray-800 to-gray-900",
                delay: 0.2
              },
              {
                title: "Sürdürülebilir Teknolojiler",
                subtitle: "Çevresel Sorumluluk",
                icon: FaLeaf,
                description: "Karbon ayak izi azaltılmış, geri dönüştürülebilir kompozit teknolojileri ve döngüsel ekonomi odaklı endüstriyel çözümler",
                achievements: [
                  "%100 geri dönüşüm hedefi",
                  "ISO 14001:2015 sertifikasyon",
                  "Karbon nötr üretim süreçleri",
                  "Sürdürülebilir tedarik zinciri"
                ],
                color: "from-red-700 to-red-800",
                delay: 0.4
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: item.delay }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="group relative"
              >
                {/* Background Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-3xl blur-xl"
                  style={{ 
                    background: `linear-gradient(135deg, ${item.color.split(' ')[1]} 0%, ${item.color.split(' ')[3]} 100%)` 
                  }}
                />

                {/* Card Container */}
                <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden h-full">
                  {/* Header Section with Gradient */}
                  <div className={`bg-gradient-to-r ${item.color} p-8`}>
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <item.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-white/80 text-sm font-medium uppercase tracking-wider">
                          6 Kıta
                        </div>
                        <div className="text-2xl font-bold text-white">Global</div>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-red-100 font-medium">{item.subtitle}</p>
                  </div>

                  {/* Content Section */}
                  <div className="p-8">
                    <p className="text-gray-600 mb-8 leading-relaxed">
                      {item.description}
                    </p>

                    {/* Achievements List */}
                    <div className="space-y-4 mb-8">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        Başarı Göstergeleri
                      </h4>
                      {item.achievements.map((achievement, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-gradient-to-r from-red-500 to-red-600 rounded-full" />
                          <span className="text-gray-700 font-medium">
                            {achievement}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Global Presence Indicator */}
                    <div className="pt-6 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FaGlobe className="w-5 h-5 text-gray-400" />
                          <span className="text-sm font-medium text-gray-600">
                            Küresel Varlık
                          </span>
                        </div>
                        <div className="flex -space-x-2">
                          {[1, 2, 3, 4, 5, 6].map((num) => (
                            <div
                              key={num}
                              className="w-8 h-8 rounded-full border-2 border-white bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center"
                            >
                              <span className="text-xs font-bold text-white">
                                {num}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hover Indicator */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cards Section */}
      <section className="py-20 bg-white">
        <Cards2 />
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        {/* Red Accent Lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-red-700" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-red-700" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center">
                  <FaShieldAlt className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-red-400 uppercase tracking-wider">Vizyonumuz</span>
              </div>
              <h3 className="text-4xl font-bold text-white mb-6">
                Sürdürülebilir Gelecek İçin
                <span className="block text-red-300">İnovatif Çözümler</span>
              </h3>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                Kompozit teknolojileri ile endüstrilerin dönüşümüne liderlik ediyor, 
                çevre dostu çözümlerimizle <span className="font-semibold text-white">6 kıtada</span> sürdürülebilir bir gelecek inşa ediyoruz.
              </p>
              <div className="flex items-center gap-8">
                <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm border border-white/20">
                  <div className="text-3xl font-bold text-white">ISO 9001</div>
                  <div className="text-sm text-red-300">Kalite Yönetimi</div>
                </div>
                <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm border border-white/20">
                  <div className="text-3xl font-bold text-white">ISO 14001</div>
                  <div className="text-sm text-red-300">Çevre Yönetimi</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 shadow-2xl"
            >
              <div className="flex items-center gap-3 mb-8">
                <FaUsers className="w-8 h-8 text-red-400" />
                <h4 className="text-2xl font-bold text-white">Global Ekibimiz</h4>
              </div>
              <div className="space-y-4">
                {[
                  { region: 'Avrupa', team: '250+', color: 'bg-red-600' },
                  { region: 'Asya Pasifik', team: '180+', color: 'bg-gray-700' },
                  { region: 'Kuzey Amerika', team: '120+', color: 'bg-red-700' },
                  { region: 'Orta Doğu & Afrika', team: '95+', color: 'bg-gray-600' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-5 bg-gray-800/50 rounded-xl hover:bg-gray-800 transition-colors">
                    <div>
                      <div className="font-semibold text-white">{item.region}</div>
                      <div className="text-sm text-gray-400">{item.team} profesyonel</div>
                    </div>
                    <div className={`w-3 h-3 ${item.color} rounded-full animate-pulse`} />
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-gray-700">
                <div className="text-center">
                  <div className="text-5xl font-bold text-white mb-2">6</div>
                  <div className="text-sm text-gray-400">Kıtada İnsan Kaynağı</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;